import { Quaternion, Vector3 } from "three";
import type { ZoneWarpPoint } from "../loaders/ifo";

interface Point2D {
    x: number;
    y: number;
}

// Matches the horizontal character radius used by CollisionDetector. The IFO
// warp scale describes the marker footprint; padding it by the character
// radius makes contact occur when the two footprints touch rather than only
// when the player's center enters the gate.
export const CHARACTER_CONTACT_RADIUS = 0.5;

const _local = new Vector3();
const _invRot = new Quaternion();

/**
 * True when the player touches the warp's footprint: a ±1 unit box scaled by
 * the IFO scale and oriented by the IFO rotation (same shape as the editor
 * marker). Height is ignored.
 */
export function isInWarpFootprint(worldPosition: Point2D, mapOrigin: Point2D, warp: ZoneWarpPoint): boolean {
    _local.set(
        worldPosition.x - (mapOrigin.x + warp.position.x),
        worldPosition.y - (mapOrigin.y + warp.position.y),
        0
    );
    const r = warp.rotation;
    if (r) _local.applyQuaternion(_invRot.set(r.x, r.y, r.z, r.w).invert());
    const halfX = Math.abs(warp.scale?.x ?? 0) + CHARACTER_CONTACT_RADIUS;
    const halfY = Math.abs(warp.scale?.y ?? 0) + CHARACTER_CONTACT_RADIUS;
    return Math.abs(_local.x) <= halfX && Math.abs(_local.y) <= halfY;
}

/** Finds the first IFO warp marker whose horizontal footprint touches the player. */
export function findWarpContact(
    worldPosition: Point2D,
    mapOrigin: Point2D,
    warps: ZoneWarpPoint[]
): ZoneWarpPoint | null {
    for (const warp of warps) {
        if (!warp.position || warp.warpId <= 0) continue;
        if (isInWarpFootprint(worldPosition, mapOrigin, warp)) return warp;
    }
    return null;
}
